/**
 * openapi-generatorで生成されたAPIコールをラップする
 * @param ApiClass
 * @param apiCallFunction
 */
import { AxiosResponse } from "axios";

import { getAxiosInstance, getBaseUri } from "./axios";
import { ErrorResponseBody, ValidationErrorResponseBody } from "./clients";
import { BaseAPI } from "./clients/base";
import {
  ApiConnectionError,
  ApiResponseError,
  ApiValidationError,
} from "./errors";

type NonSuccessResponseBody = ValidationErrorResponseBody | ErrorResponseBody;

const isSuccessResponse = <Body>(body: Body, status: number): body is Body =>
  status >= 200 && status < 300;

const isValidationErrorResponse = (
  body: NonSuccessResponseBody,
  status: number,
): body is ValidationErrorResponseBody => status === 422;

const isErrorResponse = (
  body: NonSuccessResponseBody,
  status: number,
): body is ErrorResponseBody => status >= 400;

async function parseResponse<Body>(
  response: AxiosResponse<Body>,
): Promise<Body> {
  if (isSuccessResponse<Body>(response.data, response.status)) {
    return Promise.resolve(response.data);
  }

  if (isValidationErrorResponse(response.data, response.status)) {
    throw new ApiValidationError(response.data);
  }

  if (isErrorResponse(response.data, response.status)) {
    throw new ApiResponseError(response.data, response.status);
  }

  throw new ApiConnectionError(response);
}

type ApiCallFunction<Args extends unknown[], Body> = (
  ...args: Args
) => Promise<AxiosResponse<Body>>;

export function wrap<Args extends unknown[], Body>(
  ApiClass: typeof BaseAPI,
  apiCallFunction: ApiCallFunction<Args, Body>,
) {
  return async (...args: Args) => {
    const instance = new ApiClass(undefined, getBaseUri(), getAxiosInstance());
    const response = await apiCallFunction.call(instance, ...args);
    return parseResponse<Body>(response);
  };
}
